import type { CreateJobPayload, Job, JobsListParams, JobsListResponse, LineItemsResponse, NoteEntry } from '../types';
import { apiClient } from './client';
import type { LineItemsResult } from '@jobmate/types'; // #67 shared-types

export interface LineItemInput {
  product_id?: number | null;
  description: string;
  quantity: string;
  unit_price: string;
  gst_applicable: boolean;
  gst_rate?: string;
  sort_order?: number;
}

export const jobs = {
  list: async (params?: JobsListParams): Promise<JobsListResponse> => {
    const { data } = await apiClient.get<JobsListResponse>('/api/jobs', { params });
    return data;
  },

  get: async (id: number): Promise<Job> => {
    const { data } = await apiClient.get<Job>(`/api/jobs/${id}`);
    return data;
  },

  create: async (payload: CreateJobPayload): Promise<Job> => {
    const { data } = await apiClient.post<Job>('/api/jobs', payload);
    return data;
  },

  update: async (id: number, payload: Partial<Job>): Promise<Job> => {
    const { data } = await apiClient.patch<Job>(`/api/jobs/${id}`, payload);
    return data;
  },

  transition: async (
    id: number,
    action: 'quote' | 'accept' | 'schedule' | 'complete' | 'pay' | 'reject' | 'cancel' | 'reschedule' | 'archive',
    payload?: Record<string, unknown>,
  ): Promise<Job> => {
    const { data } = await apiClient.post<Job>(`/api/jobs/${id}/${action}`, payload ?? {});
    return data;
  },

  getNotes: async (id: number): Promise<{ notes: NoteEntry[] }> => {
    const { data } = await apiClient.get<{ notes: NoteEntry[] }>(`/api/jobs/${id}/notes`);
    return data;
  },

  addNote: async (id: number, text: string): Promise<{ note: NoteEntry }> => {
    // Backend returns note directly (no envelope) — wrap here so callers get { note }
    const { data } = await apiClient.post<NoteEntry>(`/api/jobs/${id}/notes`, { text });
    return { note: data };
  },
};

export const lineItems = {
  getLineItems: async (jobId: number): Promise<LineItemsResponse> => {
    // After #67 rework: LineItemsResponse aligns with LineItemsResult (lineItems key, number fields).
    const { data } = await apiClient.get<LineItemsResult>(`/api/jobs/${jobId}/line-items`);
    return data as LineItemsResponse;
  },

  addLineItem: async (jobId: number, input: LineItemInput): Promise<LineItemsResponse> => {
    const { data } = await apiClient.post<LineItemsResult>(`/api/jobs/${jobId}/line-items`, input);
    return data as LineItemsResponse;
  },

  updateLineItem: async (
    jobId: number,
    itemId: number,
    input: Partial<LineItemInput>,
  ): Promise<LineItemsResponse> => {
    const { data } = await apiClient.put<LineItemsResult>(`/api/jobs/${jobId}/line-items/${itemId}`, input);
    return data as LineItemsResponse;
  },

  deleteLineItem: async (jobId: number, itemId: number): Promise<LineItemsResponse> => {
    const { data } = await apiClient.delete<LineItemsResult>(`/api/jobs/${jobId}/line-items/${itemId}`);
    return data as LineItemsResponse;
  },

  reorderLineItems: async (
    jobId: number,
    items: { id: number; sort_order: number }[],
  ): Promise<LineItemsResponse> => {
    const { data } = await apiClient.patch<LineItemsResult>(`/api/jobs/${jobId}/line-items/reorder`, { items });
    return data as LineItemsResponse;
  },
};
